import React, { useEffect, useState } from "react"

import { fetchUserAttributes } from "aws-amplify/auth"
import { useForm } from "react-hook-form"

import { ButtonElement } from "@front-shared/elements/button.element"
import { CheckboxElement } from "@front-shared/elements/checkbox.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { AUTH_CONFIG, LoginStep, MFAType } from "../../../config/auth.config"
import { OtpInputElement } from "../../../elements/otpInput.element"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import {
    authSetLoginStep,
    authSetMfaChallengePhoneNumber,
} from "../../../store/slices/auth.slice"
import type { ICommonMFAProps } from "../../../types/props.types"
import { ResendSmsCodeComponent } from "./resendSmsCode.component"

export const CodeForm: React.FunctionComponent<ICodeFormProps> = props => {
    const { handleSubmit, watch, control } = useForm()
    const dispatch = useAppDispatch()

    const [rememberDevice, setRememberDevice] = useState<boolean>()
    const [isMFASetupFlow, setIsMFASetupFlow] = useState<boolean>()

    const code = watch("code")

    // Need to check if user is logged in already
    // If yes -> it's MFA setup flow
    // If not -> it's regular MFA challenge during login -> in this case we allow to remember device
    useEffect(() => {
        const checkIsMFASetupFlow = async () => {
            try {
                await fetchUserAttributes()

                setIsMFASetupFlow(true)
            } catch (e) {
                setIsMFASetupFlow(false)
            }
        }

        checkIsMFASetupFlow()
    }, [])

    // Cleanup
    useEffect(() => {
        return () => {
            dispatch(authSetMfaChallengePhoneNumber(null))
        }
    }, [])

    const onSubmit = (data: Record<string, string> | { code: string }) => {
        props.onSubmit(data.code, rememberDevice)
    }

    const onBack = () => {
        if (isMFASetupFlow && props.mfaType === MFAType.TOTP) {
            dispatch(authSetLoginStep(LoginStep.MFA_SELECTION))

            return
        }

        if (isMFASetupFlow && props.mfaType === MFAType.SMS) {
            dispatch(authSetLoginStep(LoginStep.MFA_PHONE_COLLECTION))

            return
        }

        if (!isMFASetupFlow) {
            dispatch(authSetLoginStep(LoginStep.PASSWORD))

            return
        }
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
                <OtpInputElement
                    name="code"
                    className="mb-[24px]"
                    reactHookControl={control}
                    autoFocus={true}
                />

                {isMFASetupFlow === false && (
                    <CheckboxElement
                        className="mb-[24px]"
                        checkboxClassName="!flex !items-start"
                        name="rememberDevice"
                        onChange={setRememberDevice}
                        defaultChecked={true}
                        label={
                            <PrimaryText>
                                Remember this device for{" "}
                                {AUTH_CONFIG.rememberedDeviceExpirationDays}{" "}
                                days
                            </PrimaryText>
                        }
                    />
                )}

                {props.mfaType === MFAType.SMS && (
                    <ResendSmsCodeComponent className="mb-[24px]" />
                )}

                <div className="flex justify-between mb-[24px]">
                    <ButtonElement
                        label="Back"
                        type="default"
                        htmlType="button"
                        size="large"
                        disabled={props.isCtaLoading}
                        onClick={onBack}
                    />

                    <ButtonElement
                        label={props.submitText || "Submit"}
                        type="primary"
                        htmlType="submit"
                        size="large"
                        isLoading={props.isCtaLoading}
                        disabled={
                            !code || code.length !== AUTH_CONFIG.mfaCodeLength
                        }
                    />
                </div>
            </form>
        </div>
    )
}

interface ICodeFormProps extends ICommonMFAProps {
    onSubmit: (code: string, rememberDevice?: boolean) => void
    mfaType: MFAType
    submitText?: string
}
